import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';

import { Loading } from 'uikit/loaders';
import { Typo } from 'uikit/content';

import VersioningPropertiesBoolean from './versioningPropertiesBoolean';
import VersioningPropertiesList from './versioningPropertiesList';
import VersioningPropertiesText from './versioningPropertiesText';
import VersioningPropertiesUpload from './versioningPropertiesUpload';

import styles from '../styles/versioningProperties.module.scss';

const TYPE_MAP = {
  AutoCompleteSearch: VersioningPropertiesList,
  Boolean: VersioningPropertiesBoolean,
  Breadcrumbs: VersioningPropertiesList,
  Date: VersioningPropertiesText,
  List: VersioningPropertiesList,
  Radio: VersioningPropertiesBoolean,
  ReplaceVideo: VersioningPropertiesUpload,
  Select: VersioningPropertiesText,
  SelectTopic: VersioningPropertiesList,
  Text: VersioningPropertiesText,
  TextArea: VersioningPropertiesText,
  Upload: VersioningPropertiesUpload,
};

const VersioningProperties = ({
  isLoading = false,
  model = {},
  schema = [],
  loadingTestId = 'loading-spinner',
}) => {
  if (isLoading) return <Loading className={styles.loader} testId={loadingTestId} />;

  const mappedSections = schema.map(({ title, schema: subSchema } = {}) => {
    if (!subSchema) return;

    const schemaModel = { ...model, ...model.custom_fields };

    const mappedSchemaFields = Object.entries(subSchema).map(
      ([key, { type, label, options } = {}]) => {
        if (!type) return;

        const Component = TYPE_MAP[type];

        if (!Component) {
          console.warn(
            `Please create a new component in the type map. Field ${key}, ${type} does not exist.`,
          );
          return;
        }

        const value = schemaModel[key];

        const isRadioOrBooleanEmpty =
          !['Radio', 'Boolean'].includes(type) && (!options || options.length === 0);
        const isListEmpty = type === 'SelectTopic' && !value;
        const isArrayEmpty = Array.isArray(value) && value.length === 0;
        const isUploadEmpty = (type === 'Upload' && !value) || (type === 'ReplaceVideo' && !value);

        return (
          <div key={key} className={styles.field}>
            <Typo variant="b02" weight="medium">
              {label}
            </Typo>
            {(!value && isRadioOrBooleanEmpty) || isArrayEmpty || isUploadEmpty || isListEmpty ? (
              <Typo variant="b01" color="secondary" data-testid="not-set">
                {i18n.t('not-set')}
              </Typo>
            ) : (
              <Component key={key} options={options} value={value} />
            )}
          </div>
        );
      },
    );

    return (
      <div key={title}>
        <Typo variant="d02" className={styles.title}>
          {title}
        </Typo>
        <div className={styles.fields}>{mappedSchemaFields}</div>
      </div>
    );
  });

  return <div className={styles.element}>{mappedSections}</div>;
};

VersioningProperties.propTypes = {
  isLoading: PropTypes.bool,
  model: PropTypes.shape({}),
  schema: PropTypes.arrayOf(PropTypes.shape({})),
  loadingTestId: PropTypes.string,
};

export default React.memo(VersioningProperties);
